import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Product } from '../../../lib/api/models';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';
import { T } from '@tolgee/react';
import api from '../../../lib/api';
import ProductVariants from '../components/ProductVariants';
import { ProductVariant } from '../../../lib/api/models';
import { ProductImage } from '../../../components/ui/OptimizedImage';

const normalizeRef = (value: unknown) => String(value || '').trim().toUpperCase();

const variantMatchesRef = (variant: ProductVariant, ref: string) => {
  const normalizedRef = normalizeRef(ref);
  if (!normalizedRef) return false;

  const candidateRefs = [
    variant.sku,
    (variant as any).ref,
    (variant as any).reference,
    (variant as any).ean,
    (variant as any).barcode,
    (variant as any).code,
  ];

  return candidateRefs.some(candidate => normalizeRef(candidate) === normalizedRef);
};

const ProductDetailPage = ({ productId: propProductId }: { productId?: string } = {}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const requestedSku = searchParams.get('sku') || '';
  const id = propProductId || paramId;
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab, setActiveTab] = useState('ingredients');

  const { addItem } = useCart();
  const { auth } = useAuth();
  const { trackProductViewed, trackProductAdded } = useZarazTracking();
  const isAuthenticated = auth.isAuthenticated;

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      setError(null);

      try {
        if (id) {
          const response = await api.client.get<Product>(`/products/${id}`);

          console.log('📦 [ProductDetailPage] Response completa:', response);
          console.log('📦 [ProductDetailPage] Product data:', response.data);
          console.log('📦 [ProductDetailPage] Variants:', response.data?.variants);
          console.log('📦 [ProductDetailPage] Images:', response.data?.images);
          console.log('📦 [ProductDetailPage] Price:', response.data?.price);
          console.log('📦 [ProductDetailPage] Category:', response.data?.category);

          if (response.success && response.data) {
            // Mapear _id a id para imágenes si es necesario
            const productData = response.data;
            if (productData.images) {
              productData.images = productData.images.map((img: any) => ({
                ...img,
                id: img.id || img._id
              }));
            }

            // Mapear _id a id para variantes si es necesario
            if (productData.variants) {
              productData.variants = productData.variants.map((variant: any) => ({
                ...variant,
                id: variant.id || variant._id
              }));
            }

            setProduct(productData);
            // Pre-seleccionar variante por referencia si viene en la URL (?sku=...)
            let initialVariant = productData.variants?.[0] || null;
            if (requestedSku && productData.variants) {
              const matched = productData.variants.find((variant: ProductVariant) => variantMatchesRef(variant, requestedSku));
              if (matched) initialVariant = matched;
            }
            setSelectedVariant(initialVariant);
            trackProductViewed(productData, initialVariant);
          } else {
            setError(response.error?.message || 'Producto no encontrado');
          }
        }
      } catch (error) {
        console.error("Error al cargar el producto:", error);
        setError('Error al cargar el producto. Inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, requestedSku, trackProductViewed]);

  // Reset selected image when variant changes
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedVariant]);

  // SEO dinámico
  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} | Jarad Pet Food`;
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;

    console.log('🛒 [ProductDetailPage] Adding to cart:', {
      product: product.name,
      variant: selectedVariant,
      variantSku: selectedVariant?.sku,
      quantity
    });

    addItem(product, selectedVariant, quantity);
    trackProductAdded(product, selectedVariant, quantity);

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 3000);
  };

  // Obtener precio actual
  const getCurrentPrice = () => {
    if (selectedVariant?.price) return selectedVariant.price;
    return product?.price || 0;
  };

  const getCurrentStock = () => {
    if (selectedVariant) return selectedVariant.stock;
    return product?.stock || 0;
  };

  // Get packaging info from selected variant
  const getPackagingInfo = () => {
    const packaging = selectedVariant?.packaging;
    return {
      minimumQuantity: packaging?.minimumQuantity || 1,
      quantityStep: packaging?.quantityStep || 1,
      presentation: packaging?.presentation || '',
      description: packaging?.description || ''
    };
  };

  const packagingInfo = getPackagingInfo();

  // Reset quantity to minimum when variant changes
  useEffect(() => {
    const minQty = selectedVariant?.packaging?.minimumQuantity || 1;
    setQuantity(minQty);
    setQuantityInput(String(minQty));
  }, [selectedVariant]);

  // Compute quantity validation
  const getQuantityValidation = () => {
    const val = Number(quantityInput);
    const { minimumQuantity, quantityStep } = packagingInfo;

    if (quantityInput === '' || isNaN(val)) {
      return { valid: false, message: null }; // empty while typing, no error message
    }

    if (val < minimumQuantity) {
      return {
        valid: false,
        message: `La cantidad mínima es ${minimumQuantity} unidades`,
      };
    }

    if (quantityStep > 1 && val % quantityStep !== 0) {
      const nextValid = Math.ceil(val / quantityStep) * quantityStep;
      return {
        valid: false,
        message: `La cantidad debe ser múltiplo de ${quantityStep}. Cantidad válida más cercana: ${nextValid}`,
      };
    }

    return { valid: true, message: null };
  };

  const quantityValidation = getQuantityValidation();

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setQuantityInput(raw);

    const val = Number(raw);
    if (raw !== '' && !isNaN(val) && val > 0) {
      setQuantity(val);
    }
  };

  const handleQuantityBlur = () => {
    const { minimumQuantity, quantityStep } = packagingInfo;
    let val = Number(quantityInput);

    if (isNaN(val) || val < minimumQuantity) {
      val = minimumQuantity;
    } else if (quantityStep > 1 && val % quantityStep !== 0) {
      val = Math.ceil(val / quantityStep) * quantityStep;
    }

    setQuantity(val);
    setQuantityInput(String(val));
  };

  const handleStepDown = () => {
    const newVal = Math.max(packagingInfo.minimumQuantity, quantity - packagingInfo.quantityStep);
    setQuantity(newVal);
    setQuantityInput(String(newVal));
  };

  const handleStepUp = () => {
    const newVal = quantity + packagingInfo.quantityStep;
    setQuantity(newVal);
    setQuantityInput(String(newVal));
  };

  const hasDiscount = selectedVariant?.discountPrice && selectedVariant.discountPrice > selectedVariant.price;

  // Determine which images to display: variant images if available, otherwise product images
  const displayImages = (selectedVariant?.images && selectedVariant.images.length > 0)
    ? selectedVariant.images
    : product?.images || [];

  if (loading) {
    return (
      <div className="bg-white">
        <div className="max-w-[1500px] mx-auto px-2 sm:px-3 lg:px-4 py-4">
          {/* Breadcrumbs Skeleton */}
          <div className="mb-4">
            <div className="flex gap-2">
              <div className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded w-1 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded w-1 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
            </div>
          </div>

          {/* Layout principal */}
          <div className="lg:grid lg:grid-cols-12 lg:gap-6">
            {/* Columna izquierda: Imágenes Skeleton */}
            <div className="lg:col-span-5">
              <div className="sticky top-20">
                {/* Imagen principal skeleton */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                  <div className="aspect-square bg-gray-200 animate-pulse"></div>
                </div>

                {/* Thumbnails skeleton */}
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                      <div className="aspect-square bg-gray-200 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Columna central: Info del producto Skeleton */}
            <div className="lg:col-span-4 mt-6 lg:mt-0">
              {/* Título skeleton */}
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-3 animate-pulse"></div>

              {/* Marca y categoría skeleton */}
              <div className="flex gap-4 mb-3">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              </div>

              {/* Rating skeleton */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
                <div className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
              </div>

              {/* Precio skeleton */}
              <div className="bg-gray-50 rounded-lg p-4 mb-5">
                <div className="h-10 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
              </div>

              {/* Descripción skeleton */}
              <div className="mb-5 space-y-2">
                <div className="h-5 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
              </div>

              {/* Variantes skeleton */}
              <div className="mb-6">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
              </div>

              {/* Tabs skeleton */}
              <div className="mt-8">
                <div className="border-b border-gray-200 flex gap-6 mb-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Columna derecha: Compra Skeleton */}
            <div className="lg:col-span-3 mt-6 lg:mt-0">
              <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-5 sticky top-20 shadow-sm">
                {/* Precio skeleton */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="h-3 bg-gray-200 rounded w-16 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>

                {/* Stock skeleton */}
                <div className="mb-4">
                  <div className="h-8 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>

                {/* Cantidad skeleton */}
                <div className="mb-4">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>

                {/* Botón skeleton */}
                <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>

                {/* Beneficios skeleton */}
                <div className="mt-5 pt-5 border-t border-gray-200 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-40 mb-3 animate-pulse"></div>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="flex-1 space-y-1">
                        <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4"><T keyName="product.not_found_title">Producto no encontrado</T></h2>
          <Link to="/products" className="text-blue-600 hover:underline">
            <T keyName="product.back_to_products">Volver a productos</T>
          </Link>
        </div>
      </div>
    );
  }

  const currentPrice = getCurrentPrice();
  const currentStock = getCurrentStock();
  const isOutOfStock = currentStock <= 0;

  return (
    <div className="bg-white">
      {/* Notificación de añadido al carrito */}
      {addedToCart ? (
        <div className="fixed top-20 right-4 z-50 bg-white border border-green-200 rounded-lg shadow-lg p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900"><T keyName="product.added_to_cart">Añadido al carrito</T></p>
            <p className="text-sm text-gray-500">{product.name}</p>
          </div>
        </div>
      ) : null}

      <div className="max-w-[1500px] mx-auto px-2 sm:px-3 lg:px-4 py-4">
        {/* Breadcrumbs */}
        <div className="mb-4">
          <nav className="flex text-xs">
            <Link to="/" className="text-gray-500 hover:text-gray-700"><T keyName="product.breadcrumb_home">Inicio</T></Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/products" className="text-gray-500 hover:text-gray-700"><T keyName="product.breadcrumb_products">Productos</T></Link>
            {product.category ? (
              <>
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-gray-900">{product.category.name}</span>
              </>
            ) : null}
          </nav>
        </div>

        {/* Layout principal */}
        <div className="lg:grid lg:grid-cols-12 lg:gap-6">
          {/* Columna izquierda: Imágenes (40%) */}
          <div className="lg:col-span-5">
            {/* Imagen principal */}
            <div className="sticky top-20">
              {/* Indicador de imágenes de variante */}
              {selectedVariant?.images && selectedVariant.images.length > 0 && (
                <div className="mb-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block">
                  Mostrando imágenes de: {selectedVariant.name}
                </div>
              )}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                <div className="aspect-square">
                  <ProductImage
                    src={displayImages[selectedImageIndex]?.url || '/placeholder-product.jpg'}
                    alt={selectedVariant?.name || product.name}
                    size={800}
                    fit="contain"
                    className="w-full h-full object-contain p-8"
                  />
                </div>
              </div>

              {/* Thumbnails */}
              {displayImages.length > 1 ? (
                <div className="grid grid-cols-6 gap-2">
                  {displayImages.map((image: any, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`border-2 rounded-lg overflow-hidden transition-all ${selectedImageIndex === index
                        ? 'border-orange-500'
                        : 'border-gray-200 hover:border-gray-400'
                        }`}
                    >
                      <div className="aspect-square">
                        <ProductImage
                          src={image.url}
                          alt={`${selectedVariant?.name || product.name} ${index + 1}`}
                          size={150}
                          fit="contain"
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Columna central: Info del producto (40%) */}
          <div className="lg:col-span-4 mt-6 lg:mt-0">
            {/* Título */}
            <h1 className="text-2xl font-semibold text-gray-900 mb-3 leading-tight">{product.name}</h1>

            {/* Marca, categoría y referencia */}
            <div className="flex flex-wrap items-center gap-4 mb-3">
              {product.brand ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-gray-500"><T keyName="product.brand">Marca</T>:</span>
                  <Link
                    to={`/products?brand=${product.brand}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {product.brand}
                  </Link>
                </div>
              ) : null}
              {product.category ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-gray-500"><T keyName="product.category">Categoría</T>:</span>
                  <Link
                    to={`/products?category=${product.category.slug}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {product.category.name}
                  </Link>
                </div>
              ) : null}
              {selectedVariant?.sku ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-gray-500"><T keyName="product.reference">Ref</T>:</span>
                  <span className="text-sm font-medium text-gray-900">{selectedVariant.sku}</span>
                </div>
              ) : null}
            </div>

            {/* Precio destacado */}
            <div className="bg-gray-50 rounded-lg p-4 mb-5">
              {!isAuthenticated ? (
                <div className="text-center py-2">
                  <p className="text-gray-600 mb-2">
                    <T keyName="product.login_to_see_price">Inicia sesión para ver el precio</T>
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Link
                      to={`/login?redirect=/products/${id}`}
                      className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg text-sm transition-colors"
                    >
                      <T keyName="product.login">Iniciar sesión</T>
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-lg text-sm transition-colors"
                    >
                      <T keyName="product.register">Registrarse</T>
                    </Link>
                  </div>
                </div>
              ) : hasDiscount ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded"><T keyName="product.offer">OFERTA</T></span>
                    <span className="text-sm text-gray-500 line-through">
                      {selectedVariant?.discountPrice?.toFixed(2)}€
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-red-700">
                      {currentPrice.toFixed(2)}€
                    </span>
                    <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                      <T keyName="product.discount" params={{ percent: Math.round((1 - currentPrice / (selectedVariant?.discountPrice || 1)) * 100).toString() }}>{`-${Math.round((1 - currentPrice / (selectedVariant?.discountPrice || 1)) * 100)}%`}</T>
                    </span>
                  </div>
                  <p className="text-xs text-gray-600"><T keyName="product.save">Ahorras</T> {((selectedVariant?.discountPrice || 0) - currentPrice).toFixed(2)}€</p>
                </div>
              ) : (
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {currentPrice > 0 ? `${currentPrice.toFixed(2)}€` : <T keyName="product.check_price">Consultar precio</T>}
                  </div>
                </div>
              )}
            </div>

            {/* Descripción con mejor formato */}
            <div className="mb-5">
              <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <T keyName="product.description_title">Descripción del producto</T>
              </h2>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 leading-relaxed text-sm">{product.description}</p>
              </div>
            </div>

            {product.promotionMessage?.trim() && (
              <div className="mb-5 rounded-xl border border-[#e5d7a7] bg-[#fffaf0] px-4 py-4 shadow-sm">
                <div>
                  <span className="inline-flex rounded-md bg-[#f6cf02] px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-950">
                    Promoción especial
                  </span>
                  <p className="mt-2 text-sm font-bold leading-relaxed text-gray-950">
                    {product.promotionMessage}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                    Promoción sujeta a las condiciones indicadas.
                  </p>
                </div>
              </div>
            )}

            {/* Variantes */}
            {product.variants && product.variants.length > 0 ? (
              <div className="mb-6">
                <ProductVariants
                  variants={product.variants}
                  selectedVariant={selectedVariant || undefined}
                  onVariantSelect={setSelectedVariant}
                />
              </div>
            ) : null}

            {/* Tabs de información detallada */}
            <div className="mt-8">
              {/* Tab Headers */}
              <div className="border-b border-gray-200">
                <nav className="flex gap-6 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('ingredients')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === 'ingredients'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <T keyName="product.tabs.ingredients">Ingredientes</T>
                  </button>
                  <button
                    onClick={() => setActiveTab('analysis')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === 'analysis'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <T keyName="product.tabs.analysis">Análisis Garantizado</T>
                  </button>
                  <button
                    onClick={() => setActiveTab('usage')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === 'usage'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <T keyName="product.tabs.usage">Modo de Empleo</T>
                  </button>
                  <button
                    onClick={() => setActiveTab('specs')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === 'specs'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <T keyName="product.tabs.specs">Especificaciones</T>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="py-6">
                {activeTab === 'ingredients' && (
                  <div>
                    {product.ingredients ? (
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 leading-relaxed">{product.ingredients}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic"><T keyName="product.no_ingredients">No hay información de ingredientes disponible</T></p>
                    )}
                  </div>
                )}

                {activeTab === 'analysis' && (
                  <div>
                    {product.guaranteedAnalysis && Object.values(product.guaranteedAnalysis).some(v => v) ? (
                      <div className="grid grid-cols-2 gap-4">
                        {product.guaranteedAnalysis.crudeProtein && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.crude_protein">Proteína Cruda</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.crudeProtein}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.crudeFat && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.crude_fat">Grasa Cruda</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.crudeFat}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.crudeFiber && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.crude_fiber">Fibra Cruda</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.crudeFiber}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.moisture && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.moisture">Humedad</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.moisture}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.ash && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.ash">Cenizas</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.ash}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.calcium && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.calcium">Calcio</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.calcium}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.phosphorus && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.phosphorus">Fósforo</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.phosphorus}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.omega3 && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.omega3">Omega-3</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.omega3}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.omega6 && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.omega6">Omega-6</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.omega6}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.vitamins && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg col-span-2">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.vitamins">Vitaminas</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.vitamins}</span>
                          </div>
                        )}
                        {product.guaranteedAnalysis.minerals && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg col-span-2">
                            <span className="text-sm font-medium text-gray-700"><T keyName="product.analysis_labels.minerals">Minerales</T></span>
                            <span className="text-sm font-semibold text-gray-900">{product.guaranteedAnalysis.minerals}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic"><T keyName="product.no_analysis">No hay análisis garantizado disponible</T></p>
                    )}
                  </div>
                )}

                {activeTab === 'usage' && (
                  <div>
                    {product.usageInstructions ? (
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 leading-relaxed">{product.usageInstructions}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic"><T keyName="product.no_usage">No hay instrucciones de uso disponibles</T></p>
                    )}
                  </div>
                )}

                {activeTab === 'specs' && (
                  <div>
                    {product.specifications && product.specifications.length > 0 ? (
                      <div className="space-y-3">
                        {product.specifications.map((spec: any, index: number) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">{spec.name}</span>
                            <span className="text-sm font-semibold text-gray-900">{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic"><T keyName="product.no_specs">No hay especificaciones disponibles</T></p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna derecha: Compra (20%) */}
          <div className="lg:col-span-3 mt-6 lg:mt-0">
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-5 sticky top-20 shadow-sm">
              {/* Precio */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                {!isAuthenticated ? (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-600 mb-3">
                      <T keyName="product.login_to_see_price">Inicia sesión para ver el precio</T>
                    </p>
                    <Link
                      to={`/login?redirect=/products/${id}`}
                      className="block w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg text-sm transition-colors text-center"
                    >
                      <T keyName="product.login">Iniciar sesión</T>
                    </Link>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-1"><T keyName="product.price">Precio</T></p>
                    <div className="text-2xl font-bold text-gray-900">
                      {currentPrice.toFixed(2)}€
                    </div>
                  </>
                )}
              </div>

              {isAuthenticated && (
                <>
                  {/* Stock con badge más visual */}
                  <div className="mb-4">
                    {isOutOfStock ? (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <p className="text-xs text-red-700 font-medium"><T keyName="product.out_of_stock">Producto agotado</T></p>
                      </div>
                    ) : currentStock <= 5 ? (
                      <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                        <p className="text-xs text-orange-700 font-medium">
                          <T keyName="product.only_units_available" params={{ stock: currentStock.toString() }}>{`¡Solo ${currentStock} disponibles!`}</T>
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <p className="text-xs text-green-700 font-medium"><T keyName="product.available">Disponible</T></p>
                      </div>
                    )}
                  </div>

                  {/* Cantidad con diseño mejorado */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-900 mb-2"><T keyName="product.quantity_label">Cantidad</T></label>
                    {/* Show packaging info */}
                    {packagingInfo.presentation && (
                      <p className="text-xs text-gray-500 mb-2">
                        <span className="font-medium">{packagingInfo.presentation}</span>
                        {packagingInfo.minimumQuantity > 1 && (
                          <span className="ml-2">• Mín. {packagingInfo.minimumQuantity} uds</span>
                        )}
                        {packagingInfo.quantityStep > 1 && (
                          <span className="ml-2">• Múltiplo de {packagingInfo.quantityStep}</span>
                        )}
                      </p>
                    )}
                    <div className={`flex items-center border rounded-md overflow-hidden bg-white ${!quantityValidation.valid && quantityValidation.message ? 'border-red-400' : 'border-gray-300'}`}>
                      <button
                        type="button"
                        onClick={handleStepDown}
                        disabled={quantity <= packagingInfo.minimumQuantity}
                        className="px-3 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-r border-gray-200"
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={quantityInput}
                        onChange={handleQuantityInputChange}
                        onBlur={handleQuantityBlur}
                        className="flex-1 text-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white focus:outline-none focus:ring-0"
                      />
                      <button
                        type="button"
                        onClick={handleStepUp}
                        className="px-3 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors border-l border-gray-200"
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    {!quantityValidation.valid && quantityValidation.message && (
                      <p className="mt-1.5 text-xs text-red-600 font-medium">
                        {quantityValidation.message}
                      </p>
                    )}
                  </div>

                  {/* Botón añadir al carrito */}
                  <button
                    onClick={handleAddToCart}
                    disabled={isOutOfStock || !quantityValidation.valid}
                    className={`w-full rounded-md py-2.5 px-4 text-sm font-semibold transition-all shadow-sm hover:shadow ${isOutOfStock || !quantityValidation.valid
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black hover:bg-gray-800 text-white'
                      }`}
                  >
                    {isOutOfStock ? <T keyName="product.out_of_stock">Producto agotado</T> : <T keyName="product.add_to_cart">Añadir al carrito</T>}
                  </button>
                </>
              )}

              {/* Beneficios personalizados de Jarad */}
              <div className="mt-5 pt-5 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm"><T keyName="product.why_buy_here">¿Por qué comprar en Jarad?</T></h3>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-gray-900"><T keyName="product.quality_guaranteed">Calidad garantizada</T></p>
                      <p className="text-xs text-gray-500"><T keyName="product.quality_guaranteed_desc">Productos certificados</T></p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-gray-900"><T keyName="product.secure_payment">Pago seguro</T></p>
                      <p className="text-xs text-gray-500"><T keyName="product.secure_payment_desc">Conexión encriptada</T></p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-gray-900"><T keyName="product.expert_care">Cuidado experto</T></p>
                      <p className="text-xs text-gray-500"><T keyName="product.expert_care_desc">Asesoramiento profesional</T></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
